/**
 * Dropdown options for CreateActionPlanForm
 */

export const SOURCE_OPTIONS = [
  { id: 'General', displayText: 'General' },
  { id: 'Observations', displayText: 'Observations' },
  { id: 'Shift_Handover', displayText: 'Shift Handover' },
  { id: 'Leadership_Walk', displayText: 'Leadership Walk' },
  { id: 'Sparc_Walk', displayText: 'Sparc Walk' },
  { id: 'Continuous_Improvement', displayText: 'Continuous Improvement' },
  { id: 'New_Hire_Onboarding', displayText: 'New Hire Onboarding' },
  { id: 'Racking_Inspection', displayText: 'Racking Inspection' },
  { id: 'Quality_Audit', displayText: 'Quality Audit' },
  { id: 'Site_Evaluation', displayText: 'Site Evaluation' },
];

/**
 * Mapping of display text/frontend values to backend SOURCE column values
 * Backend CHECK constraint allows: Quality_Audit, Site_Evaluation, New_Hire_Onboarding,
 * Racking_Inspection, Sparc_Walk, Shift_Handover, Leadership_Walk, Observations,
 * General, Standup, SPARC, LHE, CI_Objective
 */
export const SOURCE_TO_BACKEND_MAPPING: Record<string, string> = {
  'Quality Audit': 'Quality_Audit',
  'Site Evaluation': 'Site_Evaluation',
  'New Hire Onboarding': 'New_Hire_Onboarding',
  'Racking Inspection': 'Racking_Inspection',
  'SPARC Walk': 'Sparc_Walk',
  'Sparc Walk': 'Sparc_Walk',
  'Shift Handover': 'Shift_Handover',
  'Leadership Walk': 'Leadership_Walk',
  'Observation': 'Observations',
  'Observations': 'Observations',
  'General': 'General',
  'Standup': 'Standup',
  'SPARC': 'SPARC',
  'SPARC KPI': 'SPARC',
  'Lighthouse Execution': 'LHE',
  'Continuous Improvement': 'CI_Objective',
};

export const TYPE_OPTIONS = [
  { id: 'kpi', displayText: 'KPI' },
  { id: 'kfa', displayText: 'KFA' },
  { id: 'process', displayText: 'Process' },
];

export const PRIORITY_OPTIONS = [
  { id: 'high', displayText: 'High' },
  { id: 'medium', displayText: 'Medium' },
  { id: 'low', displayText: 'Low' },
];

/**
 * Transform email to display name
 */
export const emailToDisplayName = (email: string): string => {
  const nameFromEmail = email.split('@')[0] ?? '';
  const nameParts = nameFromEmail.split('.');

  const firstAndLastName = nameParts.slice(0, 2);
  
  let displayName = firstAndLastName
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  displayName = displayName.replace(/\s*Contractor\s*$/i, '').trim();
  return displayName || email;
};


export const formatAuthUserName = (userName: string): string => {
  if (!userName) return '';
  
  
  let name = userName.replace(/\s*\{[^}]*\}\s*/g, '').trim();
  
  name = name.replace(/\s*-\s*.*$/i, '').trim();
  
  if (name.includes(',')) {
    const parts = name.split(',').map(p => p.trim());
    if (parts.length === 2) {
      return `${parts[1]} ${parts[0]}`;
    }
  }
  
  return name;
};

/**
 * Transform API user response to assignee option
 */
export const transformUserToAssigneeOption = (user: any) => ({
  id: String(user.GPID ?? user.ID ?? ''),
  displayText: emailToDisplayName(user.UserEmail ?? ''),
  email: user.UserEmail ?? '',
  role: user.Role ?? undefined,
  role_id: user.RoleID ?? undefined,
  raw: user,
});

/**
 * Transform API employee response to employee option
 */
export const transformEmployeeToOption = (employee: any) => ({
  id: String(employee?.GPID ?? employee?.GPID ?? employee?.Id ?? employee?.ID ?? ''),
  displayText: employee?.Employee_Name ?? employee?.EmployeeName ?? '',
  raw: employee,
});

/**
 * Map API source value to dropdown option id
 * Converts "Shift Handover" → "Shift_Handover"
 * Converts "Leadership Walk" → "Leadership_Walk"
 */
export const mapApiSourceToOptionId = (apiValue: string): string => {
  if (!apiValue) return '';
  
  // Create mapping from displayText to id
  const mapping: { [key: string]: string } = {};
  SOURCE_OPTIONS.forEach(option => {
    mapping[option.displayText] = option.id;
  });
  
  return mapping[apiValue] || apiValue;
};

/**
 * Map API type value to dropdown option id
 */
export const mapApiTypeToOptionId = (apiValue: string): string => {
  if (!apiValue) return '';
  
  // API types might be uppercase, need to map to lowercase
  const mapping: { [key: string]: string } = {};
  TYPE_OPTIONS.forEach(option => {
    mapping[option.displayText.toUpperCase()] = option.id;
    mapping[option.displayText.toLowerCase()] = option.id;
    mapping[option.id.toUpperCase()] = option.id;
  });
  
  return mapping[apiValue.toUpperCase()] || mapping[apiValue.toLowerCase()] || apiValue.toLowerCase();
};

/**
 * Map API priority value to dropdown option id
 */
export const mapApiPriorityToOptionId = (apiValue: string): string => {
  if (!apiValue) return '';
  
  // API priorities might be uppercase, need to map to lowercase
  const mapping: { [key: string]: string } = {};
  PRIORITY_OPTIONS.forEach(option => {
    mapping[option.displayText.toUpperCase()] = option.id;
    mapping[option.displayText.toLowerCase()] = option.id;
    mapping[option.id.toUpperCase()] = option.id;
  });
  
  return mapping[apiValue.toUpperCase()] || mapping[apiValue.toLowerCase()] || apiValue.toLowerCase();
};

/**
 * Get display text for source option from API value
 * Handles special API values and converts underscores to spaces
 */
export const getSourceDisplayText = (apiValue: string): string => {
  if (!apiValue) return '';
  
  // Special API value mappings (backend sends these specific codes)
  const specialMappings: { [key: string]: string } = {
    'CI_Objective': 'Continuous Improvement',
    'LHE': 'Lighthouse Execution',
    'SPARC': 'SPARC KPI',
  };
  
  // Check if it's a special API value first
  if (specialMappings[apiValue]) {
    return specialMappings[apiValue];
  }
  
  // Try to find exact match in SOURCE_OPTIONS
  const option = SOURCE_OPTIONS.find(
    opt => opt.id === apiValue || opt.displayText === apiValue
  );
  if (option) {
    return option.displayText;
  }
  
  // If no match found, convert underscores to spaces and capitalize first letter of each word
  // e.g., "leadership_walk" → "Leadership Walk"
  return apiValue
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get display text for type option from API value
 */
export const getTypeDisplayText = (apiValue: string): string => {
  if (!apiValue) return '';
  const normalizedValue = apiValue.toLowerCase();
  const option = TYPE_OPTIONS.find(
    opt => opt.id.toLowerCase() === normalizedValue || opt.displayText.toLowerCase() === apiValue.toLowerCase()
  );
  if (option) {
    return option.displayText;
  }
  const upperValue = apiValue.toUpperCase();
  if (upperValue === 'KPI' || upperValue === 'KFA') {
    return upperValue;
  }
  return apiValue.charAt(0).toUpperCase() + apiValue.slice(1).toLowerCase();
};

/**
 * Get display text for priority option from API value
 */
export const getPriorityDisplayText = (apiValue: string): string => {
  if (!apiValue) return '';
  const normalizedValue = apiValue.toLowerCase();
  const option = PRIORITY_OPTIONS.find(
    opt => opt.id.toLowerCase() === normalizedValue || opt.displayText.toUpperCase() === apiValue.toUpperCase()
  );
  return option?.displayText || apiValue;
};

/**
 * Get API-formatted value for source (with underscores)
 * Transforms dropdown selection to backend format
 * Example: { id: 'Shift_Handover', displayText: 'Shift Handover' } => 'Shift_Handover'
 * Special case: Continuous Improvement maps to CI_Objective for the API
 */
export const getSourceApiValue = (sourceOption: any): string | null => {
  if (!sourceOption) return null;
  
  // Get the raw value (prioritize id over displayText)
  const rawValue = sourceOption?.id ?? sourceOption?.displayText ?? null;
  if (!rawValue) return null;
  
  // Check if this source needs special mapping for the backend
  // The SOURCE_TO_BACKEND_MAPPING handles special cases like:
  // - 'Continuous Improvement' or 'Continuous_Improvement' => 'CI_Objective'
  // - 'Lighthouse Execution' => 'LHE'
  // - 'SPARC KPI' => 'SPARC'
  const displayText = sourceOption?.displayText ?? rawValue.replace(/_/g, ' ');
  if (SOURCE_TO_BACKEND_MAPPING[displayText]) {
    return SOURCE_TO_BACKEND_MAPPING[displayText];
  }
  
  // Also check the raw value in case it's already in a mapped format
  if (SOURCE_TO_BACKEND_MAPPING[rawValue]) {
    return SOURCE_TO_BACKEND_MAPPING[rawValue];
  }
  
  return rawValue;
};

/**
 * Get API-formatted value for type (uppercase)
 * Transforms dropdown selection to backend format
 * Backend expects: KPI, KFA, PROCESS (all uppercase)
 * UI displays: KPI, KFA, Process
 * Example: { id: 'process', displayText: 'Process' } => 'PROCESS'
 */
export const getTypeApiValue = (typeOption: any): string | null => {
  if (!typeOption) return null;
  
  // If typeOption is a string, convert it to uppercase
  if (typeof typeOption === 'string') {
    return typeOption.toUpperCase();
  }
  
  // If typeOption is an object, extract displayText and convert to uppercase
  const value = typeOption?.displayText ?? typeOption?.id ?? null;
  return value ? value.toUpperCase() : null;
};

/**
 * Find matching option from list by id, email, or display name
 */
export const findMatchingOption = (
  options: any[],
  incoming: any
): any | undefined => {
  if (!incoming) return undefined;
  
  const incomingId = String(incoming?.gpid ?? incoming?.GPID ?? incoming?.id ?? incoming?.ID ?? incoming?.value ?? '');
  const incomingEmail = (incoming?.email ?? incoming?.UserEmail ?? incoming?.displayText ?? '')
    .toString()
    .toLowerCase();
  const incomingName = (incoming?.displayText ?? '')
    .toString()
    .toLowerCase();

  return options.find(
    (opt) =>
      String(opt.id) === incomingId ||
      (incomingEmail && String(opt.email).toLowerCase() === incomingEmail) ||
      (incomingName && String(opt.displayText).toLowerCase() === incomingName)
  );
};
